<template>
  <div class="steps-section" :class="{ 'compact-mode': compactMode }">
    <div class="container">
      <div class="steps-header">
        <h2 class="steps-title">Complete Your Setup</h2>
        <p class="steps-subtitle">
          Follow these steps to unlock the full potential of your platform
        </p>
      </div>

      <div v-if="!homeMode" class="progress-section">
        <div class="progress-info">
          <span class="progress-text">Setup Progress</span>
          <span class="progress-percentage">{{ progressPercentage }}%</span>
        </div>
        <v-progress-linear
          :value="progressPercentage"
          height="6"
          rounded
          color="success"
          background-color="rgba(124, 77, 255, 0.1)"
          class="progress-bar"
        />
      </div>

      <div v-if="!isInitialized" class="skeleton-container">
        <div v-for="n in 4" :key="`skeleton-${n}`" class="skeleton-card">
          <v-skeleton-loader
            class="skeleton-loader"
            :loading="true"
            :boilerplate="false"
            height="40"
            type="heading"
          />
          <v-skeleton-loader
            class="skeleton-loader"
            :loading="true"
            :boilerplate="false"
            height="60"
            type="paragraph"
          />
          <v-skeleton-loader
            class="skeleton-loader"
            :loading="true"
            :boilerplate="false"
            height="40"
            type="button"
          />
        </div>
      </div>

      <div v-else-if="filteredSteps.length > 0" class="steps-grid">
        <LaunchStepCard
          v-for="(step, idx) in filteredSteps"
          :key="step.id"
          :data-step-id="step.id"
          :step="step"
          :completed="step.completed"
          :is-current-step="isCurrentStep(step.id)"
          :can-activate="canActivateStep(step.id)"
          :compact="compactMode"
          :step-number="idx + 1"
          :home-mode="homeMode"
          @action="handleStepAction"
          @step-clicked="handleStepClick"
          @skip="handleStepSkip"
        >
          <template #data>
            <StepDataDisplay :step="step" />

            <component
              v-if="step.dialog"
              :is="step.dialog"
              v-model="step.isDialogOpen"
              @finish="completeStepForDialog(step.id)"
              @close="() => (step.isDialogOpen = false)"
            />
          </template>

          <template #dialogs>
            <component
              v-if="step.dialog"
              :is="step.dialog"
              v-model="step.isDialogOpen"
              @finish="completeStepForDialog(step.id)"
              @close="() => (step.isDialogOpen = false)"
            />
          </template>
        </LaunchStepCard>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import LaunchStepCard from './LaunchStepCard.vue';
import StepDataDisplay from './StepDataDisplay.vue';
import { createStepsArray } from '../config/stepsConfig.js';

import ImportFilesDialog from '../dialogs/ImportFilesDialog.vue';
import { PROGRAMS } from '@/access/programs';
import ImportContactsDialog from '../dialogs/ImportContactsDialog.vue';
import InviteContactsDialog from '../dialogs/InviteContactsDialog.vue';
import SetupSmsLineDialog from '../dialogs/SetupSmsLineDialog.vue';

export default {
  name: 'StepsList',
  props: {
    homeMode: {
      type: Boolean,
      default: false,
    },
    compactMode: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ImportFilesDialog,
    ImportContactsDialog,
    InviteContactsDialog,
    SetupSmsLineDialog,
    LaunchStepCard,
    StepDataDisplay,
  },
  data() {
    return {
      stepsArray: [],
      isInitialized: false,
    };
  },

  computed: {
    ...mapGetters('programs', {
      getProgram: 'getProgram',
      programsLoaded: 'loaded',
    }),
    ...mapGetters('users', [
      'hasAchievement',
      'achievementsLoaded',
      'currentUser',
    ]),
    ...mapGetters('teams', ['currentTeam']),
    ...mapState('files', {
      files: 'all',
    }),

    // Mirrors SetPhoneLineWarning.isTextingNumberSet: in a team context the phone
    // is always on the team — user credentials are not a fallback.
    hasSmsLineConfigured() {
      if (this.currentTeam) {
        return !!this.currentTeam.twilio_phone;
      }
      const { twilio_sid, twilio_ms_id, twilio_phone } = this.currentUser || {};
      return !!(twilio_sid && (twilio_phone || twilio_ms_id));
    },

    progressPercentage() {
      const completedCount = this.stepsArray.filter(
        (step) => step.completed
      ).length;
      return Math.round((completedCount / (this.stepsArray.length - 1)) * 100);
    },

    isSMSEnabled() {
      const program = this.getProgram(PROGRAMS.PRODUCTS_SMS);
      if (!this.programsLoaded || !program) return true;
      return program?.enabled;
    },

    isUserActivated() {
      return this.currentUser?.activated || false;
    },

    isReadyToInitialize() {
      return this.programsLoaded && this.achievementsLoaded;
    },

    filteredSteps() {
      if (this.homeMode) {
        return this.stepsArray.filter(
          (step) => !step.completed && step.id !== 'finish'
        );
      }
      return this.stepsArray;
    },
  },

  watch: {
    isReadyToInitialize: {
      immediate: true,
      handler(ready) {
        if (ready && !this.isInitialized) {
          this.initializeSteps();
          this.isInitialized = true;
        }
      },
    },

    isSMSEnabled: {
      handler() {
        if (this.isInitialized) {
          this.initializeSteps();
        }
      },
    },

    currentTeam: {
      handler(team) {
        if (team && this.isInitialized) {
          this.checkCompletedSteps();
        }
      },
    },

    files(newFiles) {
      if (!newFiles.length || !this.isInitialized) return;

      const filesStep = this.getStepById('files');
      if (filesStep.completed && !filesStep.isDialogOpen) {
        this.completeStep('files');
      }
    },

    achievementsLoaded: {
      immediate: true,
      handler(loaded) {
        if (loaded && this.isInitialized) {
          this.checkCompletedSteps();
        }
      },
    },
  },

  methods: {
    initializeSteps() {
      this.stepsArray = createStepsArray(
        this.isSMSEnabled,
        this.homeMode,
        this.isUserActivated
      );

      // Check completed steps after initialization
      if (this.achievementsLoaded) {
        this.checkCompletedSteps();
      }
    },

    getStepById(stepId) {
      return this.stepsArray.find((step) => step.id === stepId);
    },

    isCurrentStep(stepId) {
      const currentIndex = this.stepsArray.findIndex(
        (step) => step.completed === false
      );
      if (currentIndex === -1) return false;
      return this.stepsArray[currentIndex].id === stepId;
    },

    canActivateStep(stepId) {
      // In home mode, all steps should be available for work
      if (this.homeMode) {
        return true;
      }

      const stepIndex = this.stepsArray.findIndex((step) => step.id === stepId);
      if (stepIndex === 0) return true;

      return this.stepsArray[stepIndex - 1].completed;
    },

    checkCompletedSteps() {
      this.stepsArray.forEach((step) => {
        if (step.event && this.hasAchievement(step.event)) {
          step.completed = true;
        }

        // Check email confirmation step completion
        if (step.id === 'email-confirmation') {
          step.completed = this.isUserActivated;
        }

        // Also mark SMS step complete if phone line is already configured
        if (step.id === 'sms' && !step.completed && this.hasSmsLineConfigured) {
          step.completed = true;
        }
      });

      this.$nextTick(() => {
        this.scrollToCurrentStep();
      });
    },

    completeStep(stepId) {
      const step = this.stepsArray.find((s) => s.id === stepId);
      if (step) {
        step.completed = true;
      }
    },

    completeStepForDialog(stepId) {
      const step = this.getStepById(stepId);
      if (!step) return;

      this.completeStep(stepId);

      step.isDialogOpen = false;

      // Scroll to the next active step
      this.$nextTick(() => {
        this.scrollToCurrentStep();
      });
    },

    handleStepAction({ action, id }) {
      if (action === 'showDialog') {
        this.openDialog(id);
      } else if (action === 'finishSetup') {
        this.finishSetup();
      }
    },

    handleStepClick(stepId) {
      if (this.isCurrentStep(stepId) && stepId === 'finish') {
        this.finishSetup();
        return;
      }

      if (this.canActivateStep(stepId)) {
        const step = this.getStepById(stepId);
        if (step && step.action === 'showDialog') {
          this.openDialog(stepId);
        }
      }
    },

    handleStepSkip(stepId) {
      this.completeStep(stepId);
      // Scroll to the next active step
      this.$nextTick(() => {
        this.scrollToCurrentStep();
      });
    },

    finishSetup() {
      this.$router.push({ name: 'home' });
    },

    confirmEmail() {
      this.$emit('confirm-email');
    },

    openDialog(stepId) {
      const step = this.getStepById(stepId);
      if (step) {
        step.isDialogOpen = true;
      }
    },

    scrollToCurrentStep() {
      if (this.homeMode) return;

      const currentIndex = this.stepsArray.findIndex(
        (step) => step.completed === false
      );

      if (currentIndex === -1) return;

      const currentStepId = this.stepsArray[currentIndex].id;
      const stepElement = document.querySelector(
        `[data-step-id="${currentStepId}"]`
      );
      if (stepElement) {
        stepElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    },
  },
};
</script>

<style lang="less" scoped>
// Steps Section
.steps-section {
  margin-top: 3rem;
  margin-bottom: 32px;

  .skeleton-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 800px;
    margin: 0 auto;

    .skeleton-card {
      background: white;
      border-radius: 12px;
      padding: 24px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      border: 1px solid rgba(0, 0, 0, 0.05);
      display: flex;
      flex-direction: column;
      gap: 16px;
      height: 210px;
      justify-content: space-between;

      .skeleton-loader {
        border-radius: 6px;
        overflow: hidden;
      }

      @media (max-width: 768px) {
        height: 280px;
        padding: 20px;
      }
    }
  }

  .steps-grid {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 800px;
    margin: 0 auto;
  }

  &.compact-mode {
    margin: 24px 0 0;
    background: white;
    border-radius: @base-radius;
    padding: 16px;
    box-shadow: @base-shadow;
    border: 1px solid rgba(0, 0, 0, 0.05);

    .steps-header {
      margin-bottom: 16px;
      text-align: center;

      .steps-title {
        font-size: 22px;
      }
    }

    .steps-grid {
      gap: 18px;
      max-width: none;
    }
  }
}

// Progress Section
.progress-section {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 0.9) 100%
  );
  backdrop-filter: blur(20px);
  border: 1px solid rgba(124, 77, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  margin: 0 auto 20px;
  max-width: 800px;
  box-shadow: 0 4px 20px rgba(124, 77, 255, 0.1);

  .progress-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .progress-text {
      font-size: 0.95rem;
      font-weight: 600;
      color: var(--v-main-base);
    }

    .progress-percentage {
      font-size: 1rem;
      font-weight: 700;
      color: var(--v-success-base);
    }
  }

  .progress-bar {
    border-radius: 8px;
    overflow: hidden;
  }
}

@media (max-width: 768px) {
  .progress-section {
    padding: 12px;
    margin-bottom: 16px;

    .progress-info {
      margin-bottom: 8px;

      .progress-text {
        font-size: 0.85rem;
      }

      .progress-percentage {
        font-size: 0.9rem;
      }
    }
  }
}

@media (max-width: 480px) {
  .progress-section {
    padding: 10px;
    margin-bottom: 12px;

    .progress-info {
      .progress-text {
        font-size: 0.8rem;
      }

      .progress-percentage {
        font-size: 0.85rem;
      }
    }
  }
}

.steps-header {
  text-align: center;
  margin-bottom: 24px;
}

// Responsive adjustments
@media (max-width: 768px) {
  .hero-section {
    padding: 30px 0 20px;

    .hero-title {
      font-size: 2rem;
    }

    .hero-subtitle {
      font-size: 1rem;
    }
  }

  .steps-section {
    margin-top: 2rem;
  }

  .steps-header {
    margin-bottom: 20px;
  }

  .steps-title {
    font-size: 1.5rem;
  }

  .container {
    padding: 0 16px;
  }
}

@media (max-width: 480px) {
  .hero-section {
    padding: 20px 0 16px;

    .hero-title {
      font-size: 1.6rem;
      margin-bottom: 12px;
    }
  }

  .steps-section {
    margin-top: 1.5rem;
  }

  .steps-header {
    margin-bottom: 16px;
  }

  .steps-title {
    font-size: 1.4rem;
  }

  .container {
    padding: 0 12px;
  }

  &.compact-mode {
    .steps-header {
      margin-bottom: 12px;

      .steps-title {
        font-size: 1rem;
      }

      .steps-subtitle {
        font-size: 0.8rem;
      }
    }
  }
}
</style>

<style>
.intercom-lightweight-app {
  z-index: 100 !important;
}
</style>
